/**
 * Companion Shopify apps by the Assisty team, promoted one at a time by the
 * companion app banner (docs/features/companion-app-banner/prd.md).
 *
 * A COPY of Atlas's catalog, `Atlas/frontend/src/config/companionApps.ts`
 * (there is no shared package between the two repos). Adding an app is a
 * two-repo change:
 *   1. append the entry in Atlas and here, in the same position: catalog
 *      order is the banner's rotation order;
 *   2. pin its name + listing URL in tests/unit/config/companion-apps.spec.ts;
 *   3. save its 128px App Store icon as public/companion-apps/<id>.png,
 *      never hotlinked from the listing.
 * Only the fields the banner shows are copied; the /apps cards live in Atlas.
 */

/** Kebab-case, unique; also the `app` value in the companion_app_* events. */
export type CompanionAppId = string;

/** Where a listing link was clicked; the `utm_content` and the event `surface`. */
export type CompanionAppsSurface = 'shell_banner';

export interface CompanionApp {
  id: CompanionAppId;
  name: string;
  /** The one job this app does, one sentence; the banner shows `name: tagline`. */
  tagline: string;
  /** Pinned App Store URL WITHOUT tracking params; see `listingHref` for the link. */
  listingUrl: string;
  isFree: true;
  /** `/companion-apps/<id>.png` (128px), served from public/. */
  icon: string;
}

const ASSETS = '/companion-apps';

export const COMPANION_APPS: readonly CompanionApp[] = [
  {
    id: 'datima-product-options',
    name: 'Datima Product Options',
    tagline: 'Engraving lines, gift notes, choices, dates and uploads on your products.',
    listingUrl: 'https://apps.shopify.com/datima-product-options',
    isFree: true,
    icon: `${ASSETS}/datima-product-options.png`,
  },
  {
    id: 'datima-color-swatches',
    name: 'Datima Color Swatches',
    tagline: 'Shades and photos in place of option names on every product.',
    // Shopify gave the listing the "-1" suffix; the bare handle answers 404.
    listingUrl: 'https://apps.shopify.com/datima-color-swatches-1',
    isFree: true,
    icon: `${ASSETS}/datima-color-swatches.png`,
  },
  {
    id: 'datima-combined-listings',
    name: 'Datima Combined Listings',
    tagline: 'Separate colour products shown as one, linked by swatches.',
    listingUrl: 'https://apps.shopify.com/datima-combined-listings',
    isFree: true,
    icon: `${ASSETS}/datima-combined-listings.png`,
  },
  {
    id: 'stock-alerts',
    name: 'Assisty Stock Alerts',
    tagline: 'Know what runs out before restock, with a digest every morning.',
    listingUrl: 'https://apps.shopify.com/assisty-lowstock-alerts',
    isFree: true,
    icon: `${ASSETS}/stock-alerts.png`,
  },
  {
    id: 'dropship-pos',
    name: 'AS Dropship Purchase Orders',
    tagline: 'Turn each paid order into supplier purchase orders, tracked.',
    listingUrl: 'https://apps.shopify.com/assisty-dropship-pos',
    isFree: true,
    icon: `${ASSETS}/dropship-pos.png`,
  },
  {
    id: 'datima',
    name: 'Datima Batch Expiry Dates',
    tagline: 'Catch expiring stock before your customers do.',
    listingUrl: 'https://apps.shopify.com/datima',
    isFree: true,
    icon: `${ASSETS}/datima.png`,
  },
];

/**
 * UTM tags on every listing link so Google Analytics can attribute App Store
 * visits to Assisty; identical to Atlas so both apps report as one campaign.
 */
export const LISTING_UTM = {
  utm_source: 'assisty',
  utm_medium: 'referral',
  utm_campaign: 'companion_apps',
} as const;

export function listingHref(app: CompanionApp, surface: CompanionAppsSurface): string {
  const url = new URL(app.listingUrl);
  Object.entries(LISTING_UTM).forEach(([k, v]) => url.searchParams.set(k, v));
  url.searchParams.set('utm_content', surface);
  return url.toString();
}
